import { useUser } from "@clerk/expo";
import { FolderPlus } from "lucide-react-native";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CreateQuestion } from "@/components/questions";
import {
  AddQuestion,
  Section,
  SectionEditor,
  SortableQuestionList,
  UseCasePicker,
} from "@/components/studies";
import { SortableQuestionListRef } from "@/components/studies/SortableQuestionList";
import {
  questionHasError,
  sectionHasError,
} from "@/components/studies/completeness";
import {
  Button,
  ButtonIcon,
  ButtonText,
  Card,
  ErrorWithRetry,
  HStack,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import {
  AdhocQuestion,
  Question as QuestionType,
  Section as SectionType,
} from "@/types/resources";
import { UseCase } from "@/types/utils";

interface QuestionSection {
  data: QuestionType[];
  section: SectionType | null;
}

interface StudyEditorProps {
  disabled?: boolean;
  onNumQuestionsChange?: (size: number) => void;
  /** Reports how many questions/sections are incomplete (shown with a red
      rail) so the parent can block launch and surface the count. */
  onErrorCountChange?: (count: number) => void;
  studyId: string;
  /** When set, the study is a use-case study (single, template-locked
      section) — hides the Add Question / Add Section affordances since the
      methodology is fixed. */
  useCase?: UseCase | null;
}

const MAX_QUESTIONS = 25;

/* ── Skeleton ───────────────────────────────────────────── */

export const StudyEditorSkeleton: FC = () => (
  /* Match the loaded `SortableQuestionList`'s 12px right inset
     (`paddingRight: 12` on its scroll container) so toggling
     between loading and ready states doesn't shift the column
     horizontally. */
  <VStack className="flex-1 gap-3 pr-3">
    {Array.from({ length: 5 }).map((_, index) => (
      <Card key={index} className="border border-outline-200 rounded-xl p-4">
        <VStack className="gap-3">
          <Skeleton className="h-4 w-1/3 rounded" />
          <Skeleton className="h-3 w-full rounded" />
          <Skeleton className="h-3 w-3/4 rounded" />
          <Skeleton className="h-3 w-1/2 rounded" />
        </VStack>
      </Card>
    ))}
  </VStack>
);

/* ── Component ──────────────────────────────────────────── */

const StudyEditor: FC<StudyEditorProps> = ({
  disabled = false,
  onNumQuestionsChange,
  onErrorCountChange,
  studyId,
  useCase = null,
}) => {
  const { i18n } = useLocalization();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.admin === true;
  // Don't expose the use-case picker in production yet — jump straight to the
  // custom SectionEditor when adding a section. Super admins still get the
  // picker everywhere.
  const showUseCases = process.env.EXPO_PUBLIC_ENV !== "production" || isAdmin;

  /* "section-pick" surfaces the use-case carousel; "section-custom"
     mounts a fresh `SectionEditor` (no `section` prop) so blank sections
     get the same form (title + description, plus delete-on-save once
     persisted) as every other section in the study. Splitting the two
     keeps the picker visible for one click only and avoids re-opening
     it after the user commits to the custom path. */
  const [createMode, setCreateMode] = useState<
    "question" | "section-pick" | "section-custom" | null
  >(null);
  const [createQuestionSectionId, setCreateQuestionSectionId] = useState<
    string | null
  >(null);
  /* 1-based slot the new question will occupy in the study's flat list,
     captured from the AddQuestion gap the author clicked. Forwarded to
     CreateQuestion so the backend's mandatory `order` payload always
     reflects the click location, not the bottom of the list. */
  const [createQuestionOrder, setCreateQuestionOrder] = useState<number>(1);
  const [questions, setQuestions] = useState<QuestionType[]>([]);
  const [sections, setSections] = useState<SectionType[]>([]);

  const sortableListRef = useRef<SortableQuestionListRef>(null);

  const {
    error: questionsError,
    fetchQuestions,
    loading: loadingQuestions,
  } = API.Studies.useGetQuestions(studyId);
  const {
    error: sectionsError,
    fetchSections,
    loading: loadingSections,
  } = API.Studies.useGetSections(studyId);
  const {
    createSection,
    error: createSectionError,
    loading: creatingSection,
  } = API.Studies.useCreateSection(studyId);
  const { updateQuestion } = API.Studies.useUpdateQuestion(studyId);

  const loading = loadingQuestions || loadingSections;
  const error = questionsError || sectionsError;

  const loadData = useCallback(async () => {
    const [questionsData, sectionsData] = await Promise.all([
      fetchQuestions(),
      fetchSections(),
    ]);
    if (questionsData) {
      setQuestions(questionsData);
    }
    if (sectionsData) {
      setSections(sectionsData);
    }
  }, [fetchQuestions, fetchSections]);

  // Group questions by section, preserving order
  const groupedData = useMemo((): QuestionSection[] => {
    const sectionMap = new Map<string, SectionType>();
    sections.forEach((s) => sectionMap.set(s.id, s));

    const groups: QuestionSection[] = [];
    const sectionsWithQuestions = new Set<string>();
    let currentGroup: QuestionSection | null = null;

    questions.forEach((question) => {
      const sectionId = question.section;
      const section = sectionId ? sectionMap.get(sectionId) || null : null;

      if (section) {
        sectionsWithQuestions.add(section.id);
      }

      const currentSectionId = currentGroup?.section?.id ?? null;
      const questionSectionId = section?.id ?? null;

      if (!currentGroup || currentSectionId !== questionSectionId) {
        currentGroup = { data: [question], section };
        groups.push(currentGroup);
      } else {
        currentGroup.data.push(question);
      }
    });

    // Add empty sections (sections with no questions)
    sections
      .filter((s) => !sectionsWithQuestions.has(s.id))
      .sort((a, b) => a.order - b.order)
      .forEach((section) => {
        groups.push({ data: [], section });
      });

    return groups;
  }, [questions, sections]);

  const onQuestionUpdated = useCallback((updatedQuestion: QuestionType) => {
    setQuestions((prev) => {
      const previousQuestion = prev.find((q) => q.id === updatedQuestion.id);
      const previousRuleIds = new Set(
        previousQuestion?.rules?.map((r) => r.id)
      );

      return prev.map((q) => {
        if (q.id === updatedQuestion.id) {
          return updatedQuestion;
        }

        // Update targeted_by: remove old rules, add new ones that target this question
        const targetedByWithoutOld = q.targeted_by.filter(
          (r) => !previousRuleIds.has(r.id)
        );
        const targetedByToAdd = updatedQuestion.rules.filter(
          (r) => r.target_question_id === q.id
        );

        // Update checked_by: remove old rules, add new ones that use this question as source
        const checkedByWithoutOld = q.checked_by.filter(
          (r) => !previousRuleIds.has(r.id)
        );
        const checkedByToAdd = updatedQuestion.rules.filter(
          (r) => r.source_question_id === q.id
        );

        return {
          ...q,
          checked_by: [...checkedByWithoutOld, ...checkedByToAdd],
          targeted_by: [...targetedByWithoutOld, ...targetedByToAdd],
        };
      });
    });
  }, []);

  const onQuestionDeleted = useCallback((questionId: string) => {
    setQuestions((prev) => {
      const deletedQuestion = prev.find((q) => q.id === questionId);
      const deletedRuleIds = new Set(deletedQuestion?.rules?.map((r) => r.id));

      // Remove rules that target or use the deleted question as source
      return prev
        .filter((q) => q.id !== questionId)
        .map((q, index) => ({
          ...q,
          checked_by: q.checked_by.filter((r) => !deletedRuleIds.has(r.id)),
          order: index + 1,
          rules:
            q.rules?.filter(
              (r) =>
                r.target_question_id !== questionId &&
                r.source_question_id !== questionId
            ) || [],
          targeted_by: q.targeted_by.filter((r) => !deletedRuleIds.has(r.id)),
        }));
    });
  }, []);

  const onQuestionCreated = useCallback(
    (createdQuestion: AdhocQuestion | QuestionType) => {
      const newQuestion = createdQuestion as QuestionType;
      setQuestions((prev) => {
        // Increment order for questions at or after the new question's position
        const updated = prev.map((q) =>
          q.order >= newQuestion.order ? { ...q, order: q.order + 1 } : q
        );
        // Insert and sort by order
        return [...updated, newQuestion].sort((a, b) => a.order - b.order);
      });
      pendingScrollToQuestionId.current = newQuestion.id;
      setCreateMode(null);
      setCreateQuestionSectionId(null);
    },
    []
  );

  const handleCancelCreateQuestion = useCallback(() => {
    setCreateMode(null);
    setCreateQuestionSectionId(null);
  }, []);

  const handleAddQuestion = useCallback(
    (order: number, sectionId: string | null) => {
      setCreateQuestionOrder(order);
      setCreateQuestionSectionId(sectionId);
      setCreateMode("question");
      /* Scroll the freshly-mounted form into view. The id matches the
         `id` we set on the wrapping VStack inside renderGap; the
         setTimeout gives React a tick to actually mount the form first. */
      setTimeout(() => {
        sortableListRef.current?.scrollToItem(
          `create-question-${order}-${sectionId ?? "root"}`
        );
      }, 50);
    },
    []
  );

  const onSectionUpdated = useCallback((updatedSection: SectionType) => {
    setSections((prev) =>
      prev.map((s) => (s.id === updatedSection.id ? updatedSection : s))
    );
  }, []);

  const onSectionDeleted = useCallback((sectionId: string) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
    /* Backend cascades section delete to its questions and re-orders the
       remaining ones contiguously (see SectionsManager.delete) — mirror
       both effects locally so the UI stays consistent without a refetch. */
    setQuestions((prev) =>
      prev
        .filter((q) => q.section !== sectionId)
        .sort((a, b) => a.order - b.order)
        .map((q, i) => ({ ...q, order: i + 1 }))
    );
    setCreateMode(null);
    setCreateQuestionSectionId(null);
  }, []);

  /**
   * Picker callback: `useCase: null` means the user picked "Custom" — hand
   * off to the inline `SectionEditor` (create mode) so the user gets the
   * same title + description form as anywhere else. Otherwise create the
   * section from the matching template; the response carries the question
   * battery the backend just materialized, which we splice into our local
   * list.
   */
  const handlePickSection = useCallback(
    async (useCase: UseCase | null) => {
      if (useCase === null) {
        setCreateMode("section-custom");
        return;
      }
      const created = await createSection({ use_case: useCase });
      if (created) {
        setSections((prev) => [...prev, created.section]);
        setQuestions((prev) => [...prev, ...created.questions]);
        setCreateMode(null);
      }
    },
    [createSection]
  );

  /**
   * `SectionEditor` create-mode save. Custom sections never come with a
   * pre-baked question battery, so after persisting the section we drop
   * the user straight into the create-question form scoped to the new
   * section. The new (empty) section is appended last, so its insert gap
   * sits at order `questions.length + 1` — we MUST set `createQuestionOrder`
   * to that value (alongside the section id) or the form never mounts and
   * every "+" in the editor stays disabled behind the non-null createMode.
   */
  const handleCustomSectionSaved = useCallback(
    ({
      section: created,
    }: {
      questions: QuestionType[];
      section: SectionType;
    }) => {
      const order = questions.length + 1;
      setSections((prev) => [...prev, created]);
      setCreateQuestionOrder(order);
      setCreateQuestionSectionId(created.id);
      setCreateMode("question");
      setTimeout(() => {
        sortableListRef.current?.scrollToItem(
          `create-question-${order}-${created.id}`
        );
      }, 50);
    },
    [questions.length]
  );

  const handleCancelCreateSection = useCallback(() => {
    setCreateMode(null);
  }, []);

  const handleReorder = useCallback(
    async (
      question: QuestionType,
      newOrder: number,
      newSectionId: string | null
    ) => {
      const oldOrder = question.order;
      const oldSection = question.section;

      // Optimistic update: shift all affected orders
      setQuestions((prev) => {
        const updated = prev.map((q) => {
          if (q.id === question.id) {
            return { ...q, order: newOrder, section: newSectionId };
          }
          if (oldOrder < newOrder) {
            if (q.order > oldOrder && q.order <= newOrder) {
              return { ...q, order: q.order - 1 };
            }
          } else if (oldOrder > newOrder) {
            if (q.order >= newOrder && q.order < oldOrder) {
              return { ...q, order: q.order + 1 };
            }
          }
          return q;
        });
        return updated.sort((a, b) => a.order - b.order);
      });

      // Fire API call in background — rollback on failure
      const result = await updateQuestion(question.id, {
        lines: question.lines,
        media: question.media,
        options: question.options,
        order: newOrder,
        rules: question.rules,
        section_id: newSectionId,
        text: question.text,
      });
      if (!result) {
        setQuestions((prev) => {
          const reverted = prev.map((q) => {
            if (q.id === question.id) {
              return { ...q, order: oldOrder, section: oldSection };
            }
            // Reverse the shift
            if (oldOrder < newOrder) {
              if (q.order >= oldOrder && q.order < newOrder) {
                return { ...q, order: q.order + 1 };
              }
            } else if (oldOrder > newOrder) {
              if (q.order > newOrder && q.order <= oldOrder) {
                return { ...q, order: q.order - 1 };
              }
            }
            return q;
          });
          return reverted.sort((a, b) => a.order - b.order);
        });
      }
    },
    [updateQuestion]
  );

  const pendingScrollToQuestionId = useRef<string | null>(null);

  const scrollToItem = useCallback((id: string) => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        sortableListRef.current?.scrollToItem(id);
      }, 150);
    });
  }, []);

  // Defer the initial load to a timer so the fetchers' loading/error resets
  // don't run synchronously inside the effect.
  useEffect(() => {
    const timeout = setTimeout(() => {
      void loadData();
    }, 0);
    return () => clearTimeout(timeout);
  }, [loadData]);

  // Automatically show the create question form if no questions exist. Done
  // during render (not in an effect), keyed so it only fires when the inputs
  // change (matching the original effect's dependencies).
  const createModeKey = `${questions.length}|${loading}`;
  const [prevCreateModeKey, setPrevCreateModeKey] = useState(createModeKey);
  if (prevCreateModeKey !== createModeKey) {
    setPrevCreateModeKey(createModeKey);
    if (questions.length === 0 && !loading) {
      setCreateMode("question");
    }
  }

  useEffect(() => {
    if (onNumQuestionsChange) {
      onNumQuestionsChange(questions.length);
    }
  }, [questions.length, onNumQuestionsChange]);

  const errorCount = useMemo(
    () =>
      questions.filter((q) => questionHasError(q, questions)).length +
      sections.filter(sectionHasError).length,
    [questions, sections]
  );

  useEffect(() => {
    onErrorCountChange?.(errorCount);
  }, [errorCount, onErrorCountChange]);

  useEffect(() => {
    /* Question creation now mounts inline at the clicked AddQuestion gap
       (renderGap → CreateQuestion), so the auto-scroll only applies to
       section creation, which still lives in the listFooter at the
       bottom of the list. */
    if (createMode === "section-pick" || createMode === "section-custom") {
      setTimeout(() => {
        sortableListRef.current?.scrollToEnd();
      }, 100);
    }
  }, [createMode]);

  useEffect(() => {
    if (pendingScrollToQuestionId.current) {
      setTimeout(() => {
        if (pendingScrollToQuestionId.current) {
          scrollToItem(pendingScrollToQuestionId.current);
          pendingScrollToQuestionId.current = null;
        }
      }, 200);
    }
  }, [questions, scrollToItem]);

  const renderSectionHeader = useCallback(
    (group: QuestionSection) =>
      group.section ? (
        <Section
          disabled={disabled}
          onDeleted={() => onSectionDeleted(group.section!.id)}
          onPress={() => scrollToItem(`section-${group.section!.id}`)}
          onUpdated={onSectionUpdated}
          section={group.section}
          studyId={studyId}
        />
      ) : null,
    [disabled, onSectionDeleted, onSectionUpdated, scrollToItem, studyId]
  );

  /* AddQuestion gaps replace the old per-section / global "Add Question"
     buttons. ALWAYS rendered — no mode ever removes them — so the
     editor's vertical rhythm is the single source of spacing across
     every mode (interactive, read-only, use-case-locked, max-questions
     reached). When the gap can't take an action it renders the same
     <AddQuestion> slot in a disabled state so the layout doesn't shift.
     When this gap's (order, sectionId) matches the active create state
     we swap in the CreateQuestion form in place instead. */
  const renderGap = useCallback(
    (order: number, sectionId: string | null, disabledOverride = false) => {
      /* Use-case sections are template-locked: gaps still render so the
         vertical rhythm matches non-locked sections, but the AddQuestion
         affordance is permanently disabled (no hover, no click) since
         the question battery is part of the methodology. */
      const section = sectionId
        ? sections.find((s) => s.id === sectionId)
        : null;
      const sectionLocked = section?.use_case != null;

      /* Modes where the gap layout slot must remain visible but the
         action can never fire: a use-case study (the whole study is
         template-driven, gaps would have no valid target) and a
         non-admin who's already hit the question cap. */
      const forceDisabled =
        disabledOverride ||
        useCase !== null ||
        (!isAdmin && questions.length >= MAX_QUESTIONS);

      const isActive =
        !disabled &&
        !sectionLocked &&
        !forceDisabled &&
        createMode === "question" &&
        createQuestionOrder === order &&
        createQuestionSectionId === sectionId;

      if (isActive) {
        const formId = `create-question-${order}-${sectionId ?? "root"}`;
        /* Each side of the form gets `py-3` (12px) — the same
           gap that sits between two adjacent questions in the
           list (one full `AddQuestion` slot). Anything less and
           the form crowds into its neighbours. */
        return (
          <VStack className="py-3" id={formId}>
            <CreateQuestion
              allQuestions={questions}
              onCancel={handleCancelCreateQuestion}
              onCreated={onQuestionCreated}
              order={order}
              sectionId={sectionId ?? undefined}
              studyId={studyId}
              variables={section?.config?.variables ?? []}
            />
          </VStack>
        );
      }

      return (
        <AddQuestion
          disabled={
            disabled || sectionLocked || createMode !== null || forceDisabled
          }
          onPress={() => handleAddQuestion(order, sectionId)}
        />
      );
    },
    [
      createMode,
      createQuestionOrder,
      createQuestionSectionId,
      disabled,
      handleAddQuestion,
      handleCancelCreateQuestion,
      isAdmin,
      onQuestionCreated,
      questions,
      sections,
      studyId,
      useCase,
    ]
  );

  const listFooter = useMemo(() => {
    /* The bottom "AddQuestion" gap is rendered by SortableQuestionList
       (one trailing slot below every group), so the listFooter only
       carries the section-creation affordances. AddSection stays as a
       button — sections always append at the end of the list, so a
       single bottom button is honest UX and keeps this PR small. */
    const canAddSection =
      !disabled &&
      useCase === null &&
      !createMode &&
      (isAdmin || questions.length < MAX_QUESTIONS);

    return (
      <VStack className="gap-3">
        {canAddSection && (
          <Button
            className="self-start rounded-lg border-dashed"
            size="sm"
            onPress={() =>
              setCreateMode(showUseCases ? "section-pick" : "section-custom")
            }
            variant="outline"
          >
            <ButtonIcon
              as={FolderPlus}
              className="text-typography-600"
              size="sm"
            />
            <ButtonText className="text-sm text-typography-600">
              {i18n.t("studies.addSection")}
            </ButtonText>
          </Button>
        )}

        {!disabled && createMode === "section-pick" && (
          <Card className="border border-outline-200 rounded-xl p-4">
            <UseCasePicker
              disabled={creatingSection}
              onSelect={handlePickSection}
            />
            {createSectionError && (
              <Text className="text-error-600 text-sm mt-2">
                {createSectionError}
              </Text>
            )}
            <HStack className="justify-end items-center pt-3 mt-3 border-t border-outline-100 gap-2">
              <Button
                isDisabled={creatingSection}
                onPress={handleCancelCreateSection}
                size="sm"
                variant="outline"
              >
                <ButtonText>{i18n.t("misc.cancel")}</ButtonText>
              </Button>
            </HStack>
          </Card>
        )}

        {!disabled && createMode === "section-custom" && (
          /* Same `SectionEditor` used in edit mode — only the absence of
             a `section` prop flips it into create mode (POST + no delete
             button). The user sees the identical title + description
             form they'd see when re-opening the section later. */
          <Card className="border border-outline-200 rounded-xl p-4">
            <SectionEditor
              onCancel={handleCancelCreateSection}
              onDeleted={handleCancelCreateSection}
              onSaved={handleCustomSectionSaved}
              studyId={studyId}
            />
          </Card>
        )}

        {/* Empty-study create form: no questions AND no sections yet → no
            gaps to render into, so we surface the form directly. Once a
            section exists, its own inline gap/editor owns question creation,
            so this footer form would just sit stray outside the section. */}
        {!disabled &&
          useCase === null &&
          questions.length === 0 &&
          sections.length === 0 && (
            <CreateQuestion
              onCreated={onQuestionCreated}
              order={1}
              studyId={studyId}
            />
          )}
      </VStack>
    );
  }, [
    createMode,
    createSectionError,
    creatingSection,
    disabled,
    handleCancelCreateSection,
    handleCustomSectionSaved,
    handlePickSection,
    i18n,
    isAdmin,
    showUseCases,
    onQuestionCreated,
    questions.length,
    sections.length,
    studyId,
    useCase,
  ]);

  if (loading) return <StudyEditorSkeleton />;

  if (error) {
    return (
      <ErrorWithRetry error={error} onRetry={loadData} retrying={loading} />
    );
  }

  return (
    <SortableQuestionList
      ref={sortableListRef}
      disabled={disabled}
      groupedData={groupedData}
      listFooter={listFooter}
      onQuestionDeleted={onQuestionDeleted}
      onQuestionUpdated={onQuestionUpdated}
      onReorder={handleReorder}
      onScrollToItem={scrollToItem}
      questions={questions}
      renderGap={renderGap}
      renderSectionHeader={renderSectionHeader}
      studyId={studyId}
    />
  );
};

export default StudyEditor;
